import Kendra, { DocumentAttributeValueType } from 'aws-sdk/clients/kendra';
import _ from 'lodash';
import { DocumentAttributeTitleLookup } from '../../constants';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { facetConfiguration } from '../configuration';

dayjs.extend(utc);

interface HasLength {
    readonly length: number;
}

export const isNullOrUndefined = (it: any) => !!!it;

export const isNullOrEmpty = (it: HasLength) =>
    isNullOrUndefined(it) || it.length === 0;

export type IndexFieldNameToDocumentAttributeValueType = {
    [fieldName: string]: DocumentAttributeValueType;
};

export type DataSourceNameLookup = {
    [dataSourceId: string]: string;
} | null;

export enum DocumentAttributeValueTypeEnum {
    StringValue = 'STRING_VALUE',
    StringListValue = 'STRING_LIST_VALUE',
    LongValue = 'LONG_VALUE',
    DateValue = 'DATE_VALUE',
}

export const DocumentAttributeValueComparator: {
    [key in DocumentAttributeValueType]: (
        v1: Kendra.DocumentAttributeValue,
        v2: Kendra.DocumentAttributeValue
    ) => boolean;
} = {
    [DocumentAttributeValueTypeEnum.StringValue]: (v1, v2) =>
        v1.StringValue === v2.StringValue,
    [DocumentAttributeValueTypeEnum.StringListValue]: (v1, v2) =>
        _.isEqual(v1.StringListValue, v2.StringListValue),
    [DocumentAttributeValueTypeEnum.LongValue]: (v1, v2) =>
        v1.LongValue === v2.LongValue,
    [DocumentAttributeValueTypeEnum.DateValue]: (v1, v2) =>
        v1.DateValue?.getTime() === v2.DateValue?.getTime(),
};

export function attributeValueToString(
    value?: Kendra.DocumentAttributeValue
): string {
    if (!value) {
        return '';
    } else if (value.StringValue) {
        return value.StringValue;
    } else if (value.StringListValue) {
        return _.join(value.StringListValue, ' ');
    } else if (value.LongValue) {
        return `${value.LongValue}`;
    } else if (value.DateValue) {
        return dayjs.utc(value.DateValue).format();
    } else {
        return '';
    }
}

export function getValueType(
    value: Kendra.DocumentAttributeValue
): DocumentAttributeValueType {
    if (!isNullOrUndefined(value.LongValue)) {
        return DocumentAttributeValueTypeEnum.LongValue;
    } else if (!isNullOrUndefined(value.StringListValue)) {
        return DocumentAttributeValueTypeEnum.StringListValue;
    } else if (!isNullOrUndefined(value.DateValue)) {
        return DocumentAttributeValueTypeEnum.DateValue;
    } else {
        return DocumentAttributeValueTypeEnum.StringValue;
    }
}

export function attributeValueEquals(
    a1: Kendra.DocumentAttributeValue,
    a2: Kendra.DocumentAttributeValue
) {
    const a1ValueType = getValueType(a1);
    const a2ValueType = getValueType(a2);

    if (a1ValueType !== a2ValueType) {
        return false;
    } else {
        const comparator = DocumentAttributeValueComparator[a1ValueType];
        if (comparator) {
            return comparator(a1, a2);
        } else {
            return a1 === a2;
        }
    }
}

export function attributeValueListContains(
    attributeValueList: Kendra.DocumentAttributeValue[],
    attributeValue: Kendra.DocumentAttributeValue
) {
    if (attributeValueList) {
        return (
            attributeValueList.findIndex((a) =>
                attributeValueEquals(a, attributeValue)
            ) !== -1
        );
    } else {
        return false;
    }
}

export function attributeNameToFacetTitle(name: string): string {
    if (DocumentAttributeTitleLookup.hasOwnProperty(name)) {
        return DocumentAttributeTitleLookup[name] as string;
    } else {
        return name;
    }
}

export function attributeValueToDate(
    value?: Kendra.DocumentAttributeValue
): Dayjs {
    if (!value) {
        return dayjs.unix(0).utc();
    } else if (value.DateValue) {
        return dayjs.utc(value.DateValue);
    } else if (value.StringValue) {
        const stringAsMoment = dayjs.utc(value.StringValue);
        if (stringAsMoment.isValid()) {
            return stringAsMoment;
        }
    } else if (value.LongValue) {
        return dayjs.unix(value.LongValue).utc();
    }

    return dayjs.unix(0).utc();
}

export function selectHasReachedMaxFiltersForFacet(
    index: Kendra.DescribeIndexResponse | undefined,
    attribLists: {
        [key: string]: Kendra.DocumentAttributeValue[];
    },
    attribKey: Kendra.DocumentAttributeKey
): boolean {
    if (!index) {
        return true;
    }
    const attribList = attribLists[attribKey] || [];

    return (
        Object.keys(attribLists).length >=
            facetConfiguration.maxSelectedFacets ||
        attribList.length >= facetConfiguration.maxSelectedFacets
    );
}

export function getAttributeTypeLookup(
    index: Kendra.DescribeIndexResponse
): IndexFieldNameToDocumentAttributeValueType {
    if (
        !index?.DocumentMetadataConfigurations ||
        _.isEmpty(index.DocumentMetadataConfigurations)
    ) {
        return {};
    }

    const types: IndexFieldNameToDocumentAttributeValueType = {};
    for (const field of index.DocumentMetadataConfigurations) {
        types[field.Name] = field.Type as DocumentAttributeValueType;
    }

    return types;
}

export function getDataSourceNameLookup(
    dataSources: Kendra.DataSourceSummary[] | null
): DataSourceNameLookup {
    if (!dataSources) {
        return null;
    }

    const names: DataSourceNameLookup = {};
    for (const ds of dataSources) {
        if (ds && ds.Id && ds.Name) {
            names[ds.Id] = ds.Name;
        }
    }

    return names;
}
